import React from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import { format, isToday } from '../utils/dateUtils';

function CalendarView({
  isSignedIn,
  isLoading,
  error,
  setError,
  handleSignIn,
  weekRangeText,
  handlePrevWeek,
  handleToday,
  handleNextWeek,
  formattedDays,
  weekDates,
  calendarEvents,
  selectedEventId,
  handleEventSelect,
}) {
  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'hidden', // Prevent overflow at this level
        width: '100%',
        minWidth: 0, // Allow flexbox to shrink below content size
        display: 'flex',
        flexDirection: 'column',
        // p: { xs: 2, sm: 2 },
        bgcolor: '#020617',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: '#1E2442', color: '#FF6B6B', border: '1px solid #FF6B6B' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {!isSignedIn && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
            Connect Your Google Calendar
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#8B92B2' }}>
            Sign in with Google to view and manage your calendar events
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleSignIn}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            sx={{
              textTransform: 'none',
              bgcolor: '#5B7FFF',
              color: 'white',
              fontFamily: '"Roboto Condensed", sans-serif',
              '&:hover': {
                bgcolor: '#7B5FFF',
              },
            }}
          >
            {isLoading ? 'Loading...' : 'Sign in with Google Calendar'}
          </Button>
        </Box>
      )}

      {isSignedIn && (
        <>
          {/* Calendar Header */}
          <Box
            sx={{
              p: 2, // p-4 from reference
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              bgcolor: 'rgba(255, 255, 255, 0.01)', // bg-white/[0.01]
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
              }}
            >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontSize: '1.5rem', // text-2xl to match reference
                  fontWeight: 700,
                  letterSpacing: '-0.025em', // tracking-tight
                  m: 0,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  color: 'white',
                }}
              >
                {weekRangeText}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#94A3B8', // slate-400
                  fontSize: '0.75rem', // text-xs
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Indian Standard Time (IST)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ButtonGroup 
                variant="outlined" 
                size="small"
                sx={{ 
                  '& .MuiButton-root': {
                    borderColor: '#2A2F4A',
                    color: '#8B92B2',
                    fontFamily: '"Roboto Condensed", sans-serif',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#5B7FFF',
                      bgcolor: 'rgba(91, 127, 255, 0.1)',
                      color: '#5B7FFF',
                    },
                  },
                  '& .MuiButton-root.Mui-disabled': {
                    borderColor: '#2A2F4A',
                  }
                }}
              >
                <Button
                  onClick={handlePrevWeek}
                  sx={{ px: 1.5 }}
                >
                  &lt;
                </Button>
                <Button
                  onClick={handleToday}
                  sx={{ 
                    px: 2,
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  TODAY
                </Button>
                <Button
                  onClick={handleNextWeek}
                  sx={{ px: 1.5 }}
                >
                  &gt;
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
          </Box>

          {/* Calendar Grid - Direct grid without outer border container */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              overflowY: 'auto',
              overflowX: 'hidden', // Prevent horizontal scrolling - grid should fit
              p: 2.5,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%', // Fill parent height
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)', // 5 equal columns that adjust to available width
                gap: 1.75,
                minWidth: 0, // Allow grid to shrink below content size
              }}
            >
              {weekDates.filter(date => {
                // Filter out Saturday (6) and Sunday (0)
                const dayOfWeek = date.getDay();
                return dayOfWeek !== 0 && dayOfWeek !== 6;
              }).map((date, index) => {
                const dateKey = format(date, 'yyyy-MM-dd');
                const dayEvents = calendarEvents[dateKey] || [];
                const isTodayDate = isToday(date);
                const dayName = format(date, 'EEE').toUpperCase();
                const dayNumber = format(date, 'd');
                
                return (
                  <Box
                    key={dateKey}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%', // Fill grid cell height
                      minHeight: 0, // CRITICAL: Allow flexbox child to shrink below content size
                      minWidth: 0,
                      maxWidth: '100%', // Ensure column doesn't exceed grid cell
                      position: 'relative',
                      overflow: 'hidden', // Prevent content overflow
                    }}
                  >
                    {/* Header Card */}
                    <Box 
                      sx={{ 
                        textAlign: 'center',
                        p: 2.25,
                        mb: 2,
                        background: isTodayDate 
                          ? 'linear-gradient(to bottom, rgba(124, 58, 237, 0.2) 0%, transparent 100%)' // violet-600/20
                          : 'rgba(255, 255, 255, 0.02)', // white/[0.02]
                        borderRadius: '0.75rem 0.75rem 0 0', // rounded-t-xl
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                        borderBottom: 'none',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ 
                        fontSize: '0.625rem', // text-[10px]
                        color: isTodayDate ? '#C4B5FD' : '#64748B', // violet-300 : slate-500
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontWeight: 700,
                        mb: 0,
                        letterSpacing: '0.15em', // tracking-widest
                        textTransform: 'uppercase',
                      }}>
                        {dayName}
                      </Box>
                      <Box sx={{ 
                        fontSize: '1.25rem', // text-xl
                        color: isTodayDate ? 'white' : '#CBD5E1', // slate-300
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontWeight: 700,
                        lineHeight: 1,
                        mt: 1,
                      }}>
                        {dayNumber}
                      </Box>
                    </Box>

                    {/* Dashed Line for Empty Days */}
                    {dayEvents.length === 0 && (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.2,
                        }}
                      >
                        <Box
                          sx={{
                            width: '1px',
                            height: '100%',
                            borderRight: '1px dashed rgba(255, 255, 255, 0.3)',
                          }}
                        />
                      </Box>
                    )}

                    {/* Body Area (Events) */}
                    <Box
                      sx={{
                        flexGrow: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.02)', // white/[0.02]
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '0.75rem', // rounded-xl
                        p: 0.75,
                        position: 'relative',
                        overflowY: 'auto', // ENABLE SCROLLING FOR EACH DAY COLUMN
                        overflowX: 'hidden', // Prevent horizontal overflow
                        minWidth: 0, // Allow shrinking
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.04)', // hover:bg-white/[0.04]
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {dayEvents.map((event, eventIndex) => {
                        // Exact colors from HTML reference
                        let gradient, borderColor, borderHoverColor, textColor, timeColor;
                        
                        // Cyan/Blue theme for internal meetings
                        if (event.eventType === 'internal' || event.title.toLowerCase().includes('connect') || event.title.toLowerCase().includes('hr')) {
                          gradient = 'linear-gradient(to bottom right, rgba(8, 145, 178, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'; // cyan-600/10 to blue-600/10
                          borderColor = 'rgba(6, 182, 212, 0.2)'; // cyan-500/20
                          borderHoverColor = 'rgba(6, 182, 212, 0.4)'; // cyan-500/40
                          timeColor = '#67E8F9'; // cyan-300
                          textColor = '#A5F3FC'; // cyan-100
                        } 
                        // Orange theme for external meetings
                        else if (event.eventType === 'external' || event.title.toLowerCase().includes('standup') || event.title.toLowerCase().includes('vm')) {
                          gradient = 'linear-gradient(to bottom right, rgba(249, 115, 22, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)'; // orange-500/10 to amber-600/10
                          borderColor = 'rgba(249, 115, 22, 0.2)'; // orange-500/20
                          borderHoverColor = 'rgba(249, 115, 22, 0.4)'; // orange-500/40
                          timeColor = '#FDBA74'; // orange-300
                          textColor = '#FED7AA'; // orange-100
                        }
                        // Default theme
                        else {
                          gradient = 'linear-gradient(to bottom right, rgba(8, 145, 178, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)';
                          borderColor = 'rgba(6, 182, 212, 0.2)';
                          borderHoverColor = 'rgba(6, 182, 212, 0.4)';
                          timeColor = '#67E8F9';
                          textColor = '#A5F3FC';
                        }

                        const isSelected = selectedEventId === event.id;
                        
                        // Check if event is cancelled or declined
                        const isCancelledOrDeclined = event.status === 'cancelled' || 
                                                      event.responseStatus === 'declined' ||
                                                      event.attendees?.some(a => a.self && a.responseStatus === 'declined');
                        
                        const animationName = isCancelledOrDeclined ? 'fadeInUpDimmed' : 'fadeInUp';

                        return (
                          <Box 
                            key={eventIndex} 
                            onClick={() => handleEventSelect(event)}
                            sx={{ 
                              position: 'relative',
                              p: 1.25, // p-2.5
                              mb: 1,
                              borderRadius: '0.5rem', // rounded-lg
                              border: `1px solid ${borderColor}`,
                              background: gradient,
                              color: textColor,
                              cursor: 'pointer',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              opacity: isCancelledOrDeclined ? 0.5 : 1, // Reduced opacity for cancelled/declined
                              minWidth: 0, // Allow event cards to shrink
                              width: '100%', // Use full column width
                              '@keyframes fadeInUp': {
                                from: { opacity: 0, transform: 'translateY(10px)' },
                                to: { opacity: 1, transform: 'translateY(0)' },
                              },
                              '@keyframes fadeInUpDimmed': {
                                from: { opacity: 0, transform: 'translateY(10px)' },
                                to: { opacity: 0.5, transform: 'translateY(0)' },
                              },
                              animation: `${animationName} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                              '&:hover': {
                                boxShadow: isSelected 
                                  ? '0 0 0 2px #FFFFFF, 0 10px 15px -3px rgba(0, 0, 0, 0.2)' 
                                  : '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
                                transform: 'translateY(-1px)',
                                borderColor: isSelected ? '#FFFFFF' : borderHoverColor,
                              },
                              ...(isSelected && {
                                borderColor: '#FFFFFF',
                                boxShadow: '0 0 0 2px #FFFFFF',
                                zIndex: 10, // Ensure selected item stays on top
                              })
                            }}
                          >
                            <Box sx={{ 
                              fontSize: '0.625rem', // text-[10px]
                              color: timeColor,
                              fontWeight: 700,
                              mb: 1,
                              letterSpacing: '0.1em', // tracking-wider
                              fontFamily: 'system-ui, -apple-system, sans-serif',
                              textTransform: 'uppercase',
                              textDecoration: isCancelledOrDeclined ? 'line-through' : 'none', // Strikethrough for cancelled
                            }}>
                              {event.time}
                            </Box>
                            <Box sx={{ 
                              fontSize: '0.75rem', // text-xs
                              color: textColor,
                              fontWeight: 500,
                              lineHeight: 1.25, // leading-snug
                              mt: 1,
                              opacity: 0.9,
                              fontFamily: 'system-ui, -apple-system, sans-serif',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              textDecoration: isCancelledOrDeclined ? 'line-through' : 'none', // Strikethrough for cancelled
                            }}>
                              {event.title}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default CalendarView;
